import { actions, WatchlistsStore, WATCHLISTS_STORE_NAME } from './store';
import { frontendActions, generateApiStoreKey, getConfig, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, CmcWebApiCommon, CmcWebApiTypes } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { WATCHLIST_API_NAMES } from './apiNames';
import { subscribeWatchlistItems, unsubscribeWatchlistItems } from './watchlistItemsActions';
import { IWebApiRequestBody } from '@cmctechnology/webinvest-api-client-web-trading/dist/js/webApiBase';

type ResourceKey = number | string;

type GetState = typeof WatchlistsStore.getState;

export const selectWatchlist = (location: LocationType, id: number, updateSubscription: boolean = true) => {
  return (dispatch: Dispatch<any>, getState: GetState) => {
    const currentlySelectedId = getState()[WATCHLISTS_STORE_NAME].selectedWatchlist[location] ?? 0;

    if (currentlySelectedId === id) return;

    if (updateSubscription && currentlySelectedId) dispatch(unsubscribeWatchlistItems(location, currentlySelectedId));
    dispatch(actions.watchlistSelected({ location, id }));
    if (updateSubscription) dispatch(subscribeWatchlistItems(location, id));
  };
};

export const fetchWatchlists = () => {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const hash = getState()[WATCHLISTS_STORE_NAME].listsHash;
    const request = {
      _config: { hash }
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Watchlists.WatchListsModel1>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListsGet1,
        request,
        dispatch,
        undefined,
        false,
        true
      );
      dispatch(actions.watchlistsFetched({ response, listsHash: response._apiInfo?.Hash ?? 0 }));
    } catch {}
  };
};

export const onPollFetchWatchlists =
  (subscriptions: ResourceKey[] = []) =>
  (dispatch: Dispatch<any>) => {
    if (subscriptions.length > 0) {
      dispatch(fetchWatchlists());
    }
  };

export const subscribeWatchlists = (location: LocationType) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.subscribe({
      onPoll: onPollFetchWatchlists,
      location,
      apiName: WATCHLIST_API_NAMES.WatchListsGet1,
      storeName: WATCHLISTS_STORE_NAME,
      pollTimeout: getConfig().pollTimeout
    })
  );
};

export const unsubscribeWatchlists = (location: LocationType) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.unsubscribe({
      location,
      apiName: WATCHLIST_API_NAMES.WatchListsGet1,
      storeName: WATCHLISTS_STORE_NAME
    })
  );
};

export const createWatchlist = (name: string, instruments?: string[]) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListCreateRequest1 & IWebApiRequestBody = {
      Name: name,
      SeedStock: instruments?.join(',')!
    };

    const storeKey = generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListCreate1, name);

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Watchlists.WatchListModel2 & CmcWebApiTypes.Types.BadRequestModel>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListCreate1,
        request,
        dispatch,
        name,
        true,
        true,
        false,
        true
      );

      if (response.Id > 0) {
        dispatch(frontendActions.apiRequestSucceeded(storeKey));
        dispatch(actions.watchlistCreated({ id: response.Id, name: response.Name, count: instruments?.length ?? 0, created: Date.now().valueOf() }));
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: storeKey,
            errorCode: response?.Errors?.flatMap((e) => e.Errors).join(',')
          })
        );
      }
    } catch (error: any) {
      dispatch(
        frontendActions.apiRequestFailed({
          apiRequest: storeKey,
          errorCode: error?.errorCode
        })
      );
    }
  };
};

export const deleteWatchlist = (id: number) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListDeleteRequest1 & IWebApiRequestBody = {
      Id: id as number
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListDelete1,
        request,
        dispatch,
        id,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(actions.watchlistDeleted(id));
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListDelete1, id),
            errorCode: response.UiMessage
          })
        );
      }
    } catch {}
  };
};

export const renameWatchlist = (id: number, name: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Watchlists.WatchListRenameRequest1 & IWebApiRequestBody = {
      Id: id as number,
      Name: name
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        WATCHLISTS_STORE_NAME,
        WATCHLIST_API_NAMES.WatchListRename1,
        request,
        dispatch,
        id,
        true,
        false,
        false,
        true
      );
      if (response.Success) {
        dispatch(actions.watchlistRenamed({ id, name }));
      } else {
        dispatch(
          frontendActions.apiRequestFailed({
            apiRequest: generateApiStoreKey(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListRename1, id),
            errorCode: response.UiMessage
          })
        );
      }
    } catch {}
  };
};
